/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// ── Live feed feedback → next-batch channel ───────────────────────────────────
//
// The provider-neutral seam that carries a like/dislike from the feed card back
// to whatever source is streaming the feed. `feedback.ts` PUBLISHES each thumb
// as the user taps; a live source DRAINS the pending signals before it
// shapes the next async batch, so liking a card immediately re-shapes what
// streams in next. Firefly ignores the channel — it re-shapes server-side off
// the `setRecommendationLikeState` mutation, so a drain there is simply unread.
//
// A drained buffer, not an observable stream: the source pulls on its own
// cadence (between page yields), so there is no long-lived subscription to
// dispose and no risk of a leaked reaction on feed remount (STYLE_GUIDE §3).

/** One like/dislike the user expressed on a feed card. */
export interface FeedFeedbackSignal {
    /** Feed card id the thumb was cast on. */
    readonly cardId: string
    /** `up` = liked / more-like-this; `down` = disliked / less-like-this. */
    readonly kind: 'up' | 'down'
    /** Card heading, when known — lets a live source phrase the re-shape hint. */
    readonly title?: string
}

// Cap the buffer so a burst of taps between two page pulls can't grow unbounded
// while a slow source drains. Oldest signals are dropped first — the most
// recent taps are the ones that should shape the next batch.
const MAX_PENDING = 32

let pending: FeedFeedbackSignal[] = []

/**
 * Record a like/dislike for the next batch to reflect. Called from the card
 * feedback path on every thumb tap. Safe to call regardless of which source is
 * active — a source that doesn't consume feedback simply never drains it, and
 * `resetFeedFeedback` clears it on every feed (re)load.
 */
export function publishFeedFeedback(signal: FeedFeedbackSignal): void {
    pending.push(signal)
    if (pending.length > MAX_PENDING) pending = pending.slice(-MAX_PENDING)
}

/**
 * Take and clear every pending signal. A live source calls this before shaping
 * its next batch; the returned signals are the taps since the last drain.
 */
export function drainFeedFeedback(): readonly FeedFeedbackSignal[] {
    if (pending.length === 0) return []
    const drained = pending
    pending = []
    return drained
}

/**
 * Discard any pending signals. Called on a full feed (re)load / refresh so a
 * tap left over from a prior feed instance can't bleed into a fresh stream.
 */
export function resetFeedFeedback(): void {
    pending = []
}
